import { Box, Button, Dialog, DialogContent, Link, Typography } from "@mui/material"
import { PrintRounded } from "resource/muiImport"
import { alertConfirmDelete } from "components/Alert"
import { BoxFC, BoxFR } from "components/BoxCustom"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import FilePreviewCard from "components/FileUplaod/FilePreviewCard"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { driverColNm } from "constants/driverConstant"
import { resizeImage } from "utils/resizeImage"
import { addIdForDataGrid } from "utils"
import { printDriverAttendCheckReport } from "branch/functions/printDriverAttendCheckReport"
import dayjs from "dayjs"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"

const boxSx = { border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, gap: 0.5 }

const DetailItem = ({ label, value, width = 200 }) => (
  <BoxFR sx={{ gap: 0.5, width, alignItems: "baseline" }}>
    <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">{label}:</Typography>
    <Typography variant="body2" fontWeight="bold">
      {value === null || value === undefined || value === "" ? "-" : value}
    </Typography>
  </BoxFR>
)

const checkInColumns = [
  {
    field: "ChkInTm", headerName: "เวลาเช็คอิน", width: 150,
    valueGetter: ({ value }) => value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "",
  },
  { field: "Alc", headerName: "Alcohol", width: 80, align: "right", headerAlign: "right" },
  { field: "Temp", headerName: "อุณหภูมิ", width: 80, align: "right", headerAlign: "right" },
  { field: "Bp", headerName: "ความดัน", width: 90 },
  {
    field: "POINm", headerName: "สถานที่", flex: 1, minWidth: 140,
    valueGetter: ({ value, row }) => value || (row.Lat && row.Lng ? `${row.Lat}, ${row.Lng}` : ""),
    renderCell: ({ value, row }) => row.Lat && row.Lng
      ? <Link href={`https://www.google.com/maps/search/?api=1&query=${row.Lat},${row.Lng}`}
        target="_blank" rel="noreferrer" underline="hover">{value}</Link>
      : value,
  },
  { field: "FileCnt", headerName: "รูป", width: 60, align: "right", headerAlign: "right" },
]

const DriverAttendImageDialog = ({ selectedDrvAttnId, dialogOpen, setDialogOpen, isReadOnly }) => {

  const { ax, msData } = useContext(GlobalStateContext)
  const [filesData, setFilesData] = useState([])
  const [pendingFiles, setPendingFiles] = useState([])
  const [detailData, setDetailData] = useState(null)

  const loadFiles = useCallback(() => {
    if (!selectedDrvAttnId) return
    ax.post("/driver-attend/getDriverCheckInFile", { DrvAttnId: selectedDrvAttnId }).then(value => {
      if (value.data) {
        setFilesData(value.data)
      }
    })
  }, [ax, selectedDrvAttnId])

  const loadDetail = useCallback(() => {
    if (!selectedDrvAttnId) return
    ax.post("/driver-attend/getDriverAttendCheckInDetail", { DrvAttnId: selectedDrvAttnId }).then(value => {
      if (value.data) {
        setDetailData(value.data)
      }
    })
  }, [ax, selectedDrvAttnId])

  const handleDeleteServerFile = useCallback((file) => {
    return new Promise((resolve) => {
      alertConfirmDelete(() => {
        ax.post("/driver-attend/deleteFile", { DrvChkInFileId: file.DrvChkInFileId }).then(value => {
          if (value.data) {
            resolve()
          }
        })
      })
    })
  }, [ax])

  const handlePrint = useCallback(() => {
    if (!selectedDrvAttnId) return
    ax.post("/driver-attend/getDriverCheckInReport", { DrvAttnId: selectedDrvAttnId }).then(value => {
      if (value.data) {
        printDriverAttendCheckReport({ reportData: value.data, companyData: msData.serverData.CompanyData })
      }
    })
  }, [ax, selectedDrvAttnId, msData])

  const handleDialogOk = useCallback(async () => {
    for (const pf of pendingFiles) {
      const resizedFile = await resizeImage(pf.file, { maxWidth: 1500, maxHeight: 1500, quality: 85 })
      const formData = new FormData()
      formData.append("file", resizedFile)
      formData.append("DrvAttnId", selectedDrvAttnId)
      formData.append("FileTyp", "O")
      formData.append("Label", pf.name)
      await ax.post("/driver-attend/uploadFile", formData)
    }
    setPendingFiles([])
    setDialogOpen(false)
  }, [ax, pendingFiles, selectedDrvAttnId, setDialogOpen])

  const checkInRows = useMemo(() => addIdForDataGrid(detailData?.CheckIns || [], "DrvChkInId"), [detailData])

  useEffect(() => {
    if (dialogOpen) {
      loadFiles()
      loadDetail()
    } else {
      setFilesData([])
      setDetailData(null)
      setPendingFiles(o => {
        o.forEach(f => URL.revokeObjectURL(f.preview))
        return []
      })
    }
  }, [dialogOpen, loadFiles, loadDetail])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => {
        if (reason === "backdropClick") return
        setDialogOpen(false)
      }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth="md" >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title="ข้อมูลการลงเวลา"
        onDeleteClick={null}
      >
        <Button sx={{ml: 2,}} variant="contained" color="secondary" startIcon={<PrintRounded />}
            onClick={handlePrint}>
            พิมพ์รายงานความพร้อมก่อนปฏิบัติงาน
          </Button>
        </DialogHeader>
      <DialogContent>
        <BoxFC mt={2} sx={{ gap: 2 }}>
          <BoxFC sx={boxSx}>
            <Typography fontWeight="bold">ข้อมูลพนักงานขับรถ</Typography>
            <BoxFR sx={{ gap: 0, flexWrap: "wrap" }}>
              <DetailItem label={driverColNm.DrvCd} value={detailData?.DrvCd} width={180} />
              <DetailItem label="ชื่อ-สกุล" value={detailData?.FLNNm} width={260} />
              <DetailItem label={driverColNm.NName} value={detailData?.NName} width={160} />
              <DetailItem label={driverColNm.Tel} value={detailData?.Tel} width={180} />
              <DetailItem label={driverColNm.TukCd} value={detailData?.TukCd} width={180} />
              <DetailItem label={driverColNm.LicNm} value={detailData?.TukLicNm} width={260} />
              <DetailItem label={driverColNm.GrpNm} value={detailData?.GrpNm} width={160} />
              <DetailItem label={driverColNm.LicTyp} value={detailData?.LicTyp} width={180} />
              <DetailItem label="เลขใบขับขี่" value={detailData?.DrvLicNo} width={180} />
              <DetailItem label={driverColNm.DrvLicExp}
                value={detailData?.DrvLicExp ? dayjs(detailData.DrvLicExp).format("DD/MM/YYYY") : null} width={260} />
            </BoxFR>
          </BoxFC>

          <BoxFC sx={boxSx}>
            <Typography fontWeight="bold">ข้อมูลการลงเวลา</Typography>
            <BoxFR sx={{ gap: 0, flexWrap: "wrap" }}>
              <DetailItem label="วันที่"
                value={detailData?.DrvAttnDte ? dayjs(detailData.DrvAttnDte).format("DD/MM/YYYY") : null} width={180} />
              <DetailItem label="สถานะ" value={detailData?.DrvAttnStsNm} width={260} />
              <DetailItem label="เวลาเช็คอิน"
                value={detailData?.DrvChkInTm ? dayjs(detailData.DrvChkInTm).format("DD/MM/YYYY HH:mm") : null} width={220} />
              <DetailItem label="Alcohol" value={detailData?.Alc} width={180} />
              <DetailItem label="อุณหภูมิ" value={detailData?.Temp} width={260} />
              <DetailItem label="ความดัน" value={detailData?.BP} width={220} />
              <DetailItem label="หมายเหตุ" value={detailData?.Dscp} width={440} />
              <DetailItem label="แก้ไขล่าสุด"
                value={detailData?.ModDte
                  ? `${dayjs(detailData.ModDte).format("DD/MM/YYYY HH:mm")}${detailData.ModNm ? ` (${detailData.ModNm})` : ""}`
                  : null} width={300} />
            </BoxFR>
          </BoxFC>

          <BoxFC sx={{...boxSx, display: "none"}} >
            <Typography fontWeight="bold">การเช็คอิน ({checkInRows.length})</Typography>
            <Box height={180}>
              <DataGridCellExpand
                hideFooter
                rows={checkInRows}
                columns={checkInColumns}
              />
            </Box>
          </BoxFC>

          <BoxFC sx={boxSx} height={250}>
            <FilePreviewCard
              filesData={filesData}
              setFilesData={setFilesData}
              pendingFiles={pendingFiles}
              setPendingFiles={setPendingFiles}
              onDeleteServerFile={handleDeleteServerFile}
              fileIdKey="DrvChkInFileId"
              isReadOnly={isReadOnly}
            />
          </BoxFC>
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="บันทึก"
        cancelText={isReadOnly ? "ปิด" : null}
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={isReadOnly ? null : handleDialogOk} />
    </Dialog>
  )
}

export default React.memo(DriverAttendImageDialog)
